import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Customer-side transient state for the "agent is typing" indicator (#1770). Kept in its own
 * LiveChat slice rather than on the chat message/state model so the feature stays a clean,
 * removable seam (mirrors escalationOfferSlice). Populated only by the live SignalR push
 * (`ReceiveAgentTypingState`); never persisted.
 */
export interface AgentTypingEntry {
    isTyping: boolean;
    /**
     * Incremented on every dispatch. A human agent who keeps typing re-emits `true` every couple
     * of seconds; without a changing value the consumer wouldn't re-run its receiver-side safety
     * timer, so the timer would fire mid-typing and the dots would flicker. Bumping a counter
     * (deterministic prev+1 — the reducer stays pure) gives each ping a distinct state so the
     * safety timer resets per ping.
     */
    ping: number;
}

export interface AgentTypingState {
    // Keyed by chatId so a stale value for one chat can't leak into another.
    typing: Record<string, AgentTypingEntry>;
}

const initialState: AgentTypingState = {
    typing: {},
};

export const agentTypingSlice = createSlice({
    name: 'agentTyping',
    initialState,
    reducers: {
        setAgentTyping: (state, action: PayloadAction<{ chatId: string; isTyping: boolean }>) => {
            const { chatId, isTyping } = action.payload;
            const prev = state.typing[chatId] as AgentTypingEntry | undefined;
            state.typing[chatId] = { isTyping, ping: (prev?.ping ?? 0) + 1 };
        },
        clearAgentTyping: (state, action: PayloadAction<{ chatId: string }>) => {
            const prev = state.typing[action.payload.chatId] as AgentTypingEntry | undefined;
            state.typing[action.payload.chatId] = { isTyping: false, ping: (prev?.ping ?? 0) + 1 };
        },
    },
});

export const { setAgentTyping, clearAgentTyping } = agentTypingSlice.actions;

export default agentTypingSlice.reducer;
